import { useGateValue } from "@statsig/react-bindings";
import type { ReactNode } from "react";

import { TasksCanvasView } from "@/components/tasks/AccountTasksDialog.tsx";
import { isUserTaskSourcesItem, parseUserTaskLabelItem } from "@/types/userTaskLinks.ts";
import { TASKS_SIMPLE_LIST_GATE } from "@/types/userTasks.ts";
import { SourcesAndRulesView } from "./SourcesAndRulesView.tsx";
import { ProjectView } from "./ProjectView.tsx";
import { SimpleTasksView } from "./SimpleTasksView.tsx";
import { TaskDetailView } from "./TaskDetailView.tsx";
import { UserTasksCanvasView } from "./UserTasksCanvasView.tsx";
import { useTaskGates } from "./useTaskGates.ts";

/**
 * The Tasks pivot. Accounts in `tasks-v2` see their own task list; other
 * accounts see the per-thread task view. One Tasks page either way.
 */
export const TasksPivotView = ({
  focusTaskId,
  accountSwitcher,
  leading,
  onSelectThread,
}: {
  /**
   * The item a `/tasks/:id` link opened: a task, whose own page to show, or
   * `label-:id`, a label's.
   */
  focusTaskId: string | null;
  accountSwitcher?: ReactNode;
  leading?: ReactNode;
  onSelectThread?: (threadId: number) => void;
}) => {
  const { list: tasksV2Enabled } = useTaskGates();
  const simpleList = useGateValue(TASKS_SIMPLE_LIST_GATE);
  if (!tasksV2Enabled) {
    return (
      <TasksCanvasView
        leading={leading}
        accountSwitcher={accountSwitcher}
        onSelectThread={onSelectThread}
      />
    );
  }
  // The sources page first: it is named words where a task is digits, so the
  // test cannot catch a task, and reading it before the detail view keeps a
  // reserved word from being fetched as a task id.
  if (focusTaskId && isUserTaskSourcesItem(focusTaskId)) {
    return <SourcesAndRulesView leading={leading} accountSwitcher={accountSwitcher} />;
  }
  const labelId = focusTaskId ? parseUserTaskLabelItem(focusTaskId) : null;
  if (labelId) {
    return <ProjectView labelId={labelId} leading={leading} accountSwitcher={accountSwitcher} />;
  }
  // A task has a page of its own, so a link to one opens it.
  if (focusTaskId) {
    return (
      <TaskDetailView taskId={focusTaskId} leading={leading} accountSwitcher={accountSwitcher} />
    );
  }
  if (simpleList) {
    return <SimpleTasksView leading={leading} accountSwitcher={accountSwitcher} />;
  }
  return <UserTasksCanvasView leading={leading} accountSwitcher={accountSwitcher} />;
};
